import { $http } from "@/plugins/api";
import type { Commit } from "vuex";
import {
  Apiservice,
  Organisation,
  Uuid,
  OrganisationsTotal,
  OrganisationConnection,
} from "../../types/organisatio";
import { Params } from "@/types/api";

interface State {
  organisations: Organisation[];
  organisationsConnections: OrganisationConnection[];
  organisationsTotal: OrganisationsTotal;
  orgsPerCustomer: Organisation[];
}

const state: State = {
  organisations: [],
  organisationsConnections: [],
  organisationsTotal: { total: 0 },
  orgsPerCustomer: [],
};

const getters = {
  orginisationsList: (state: { organisations: State }) => state.organisations,
  organisationsConnections: (state: { organisationsConnections: State }) =>
    state.organisationsConnections,
  organisationsTotal: (state: { organisationsTotal: State }) =>
    state.organisationsTotal,
  orgsPerCustomer: (state: { orgsPerCustomer: State }) => state.orgsPerCustomer,
};

const actions = {
  clearOrgCache({ commit }: { commit: Commit }) {
    commit("resetState", []);
  },

  async updateOrg(
    { commit }: { commit: Commit },
    { formData, parent_uuid, organisation_type }: Apiservice,
  ) {
    const postData = {
      ...(formData.name && { name: formData.name }),
      ...(formData.country && { country: formData.country }),
      ...(parent_uuid && { parent_uuid: parent_uuid }),
      ...(formData.description && { description: formData.description }),
      ...(formData.customerID && {
        billing_system_id: Number(formData.customerID),
      }),
      ...(organisation_type && {
        organisation_type: organisation_type,
      }),
    };
    const res = await $http.patch(
      `${import.meta.env.VITE_API_CLIENT}/v1/idp/orgs/${formData.uuid}`,
      postData,
    );
    commit("updateOrg", res.data);
    return res.data;
  },

  async showOrg({ commit }: { commit: Commit }, { uuid }: Uuid) {
    const res = await $http.get(
      `${import.meta.env.VITE_API_CLIENT}/v1/idp/orgs/${uuid}?include=licences`,
    );
    commit("updateOrg", res.data);
    return res;
  },

  async getCustomerPerUsageOrg(
    { commit }: { commit: Commit },
    {
      parent_uuid,
      solution_uuid,
      limit,
      filter,
      sortdir,
      offset = 0,
      sortkey = null,
    }: unknown,
  ) {
    let url = `${import.meta.env.VITE_API_CLIENT}/v1/idp/orgs?include=licences`;

    if (parent_uuid) url += `&parent_uuid=${parent_uuid}`;
    if (solution_uuid) url += `&solution_uuid=${solution_uuid}`;
    if (limit) url += `&limit=${limit}`;
    if (filter) url += `&filter=${filter}`;
    if (sortkey) url += `&sortkey=${sortkey}`;
    if (offset) url += `&offset=${offset}`;
    if (sortdir) url += `&sortdir=${sortdir}`;

    const orgsResponse = await $http.get(url);

    commit("orgsPerCustomerList", orgsResponse.data.data);
  },

  async getOrginisationsList(
    { commit }: { commit: Commit },
    {
      parent_uuid,
      solution_uuid,
      params,
      with_connections,
    }: {
      parent_uuid: string | null;
      solution_uuid: string | null;
      params: Params;
      with_connections: boolean;
    },
  ) {
    const baseUrl = `${import.meta.env.VITE_API_CLIENT}/v1/idp/orgs`;
    const url = new URL(baseUrl);
    const reqParams = new URLSearchParams();
    reqParams.append("include", "licences");
    if (parent_uuid) {
      reqParams.append("parent_uuid", parent_uuid);
    }
    if (solution_uuid) {
      reqParams.append("solution_uuid", solution_uuid);
    }

    // Convert params to lowercase strings and append them to reqParams
    for (const [key, value] of Object.entries(params)) {
      reqParams.append(key, (value ?? "").toString().toLowerCase());
    }

    url.search = reqParams.toString();

    const orgsResponse = await $http.get(url.toString());
    const uuids = orgsResponse.data.data
      .map((obj: { uuid: string }) => obj.uuid)
      .join(",");

    if (with_connections && uuids) {
      const url = `${import.meta.env.VITE_API_CLIENT}/v1/pt/organisations/connection-status?uuids=${uuids}`;
      const test = await $http.get(url);
      commit("orgsConnections", test.data.results);
    }

    commit("orgsList", orgsResponse.data.data);
    commit("orgsTotal", orgsResponse.data.total);
    return orgsResponse.data;
  },

  async getOrganisationsConnections(
    { commit }: { commit: Commit },
    {
      uuids,
    }: {
      uuids: string;
    },
  ) {
    const url = `${import.meta.env.VITE_API_CLIENT}/v1/pt/organisations/connection-status?uuids=${uuids}`;
    const orgsResponse = await $http.get(url);
    commit("orgsConnections", orgsResponse.data.results);
  },

  // This is a new IDP organisations "batch" delete endpoint:
  async batchDeleteOrganisations(
    { commit }: { commit: Commit },
    { accounts }: { accounts: { uuid: string }[] },
  ) {
    const deletedUuidsResponse = [];

    for (const account of accounts) {
      const response = await $http.delete(
        `${import.meta.env.VITE_API_CLIENT}/v1/idp/orgs/${account.uuid}`,
        {
          _hasCustomErrorHandling: true,
        },
      );
      if (response.data && response.data.uuid) {
        deletedUuidsResponse.push(response.data.uuid);
      }
    }

    commit("deleteOrgs", deletedUuidsResponse);

    return deletedUuidsResponse;
  },

  async createPhishTitanOrg(_context: unknown, organisation_uuid: unknown) {
    await $http.post(`${import.meta.env.VITE_API_CLIENT}/v1/pt/orgs`, {
      organisationID: organisation_uuid,
      globalOrganisationID: organisation_uuid,
    });
  },

  async createOrginisation(
    { commit }: { commit: Commit },
    { formData, parent_uuid, organisation_type }: Apiservice,
  ) {
    const url = parent_uuid
      ? `${import.meta.env.VITE_API_CLIENT}/v1/idp/orgs?parent_uuid=${parent_uuid}`
      : `${import.meta.env.VITE_API_CLIENT}/v1/idp/orgs`;

    const postData: { [key: string]: unknown } = {
      ...(formData.name && { name: formData.name }),
      ...(formData.country && { country: formData.country }),
      ...(formData.description && { description: formData.description }),
      ...(formData.customerID && {
        billing_system_id: Number(formData.customerID),
      }),
      ...(organisation_type && {
        organisation_type: organisation_type,
      }),
    };
    postData.parent_uuid = parent_uuid;
    const res = await $http.post(url, postData);
    commit("updateOrgsList", res.data);
    return res;
  },
};

const mutations = {
  resetState(state: State, data: Organisation[]): void {
    state.organisations = [...data];
  },
  deleteOrgs(state: { organisations: Organisation[] }, ids: string[]) {
    for (const id of ids) {
      state.organisations = state.organisations.filter(
        (organisation) => organisation.uuid !== id,
      );
    }
  },
  orgsPerCustomerList: (
    state: { orgsPerCustomer: Organisation[] },
    data: Organisation[],
  ) => (state.orgsPerCustomer = data),
  orgsList: (state: { organisations: Organisation }, orgs: Organisation) =>
    (state.organisations = orgs),
  orgsConnections: (
    state: { organisationsConnections: OrganisationConnection[] },
    organisationsConnections: OrganisationConnection[],
  ) => (state.organisationsConnections = organisationsConnections),
  orgsTotal: (
    state: { organisationsTotal: OrganisationsTotal },
    total: number,
  ) => (state.organisationsTotal = { total: total }),
  updateOrgsList(state: { organisations: Organisation[] }, org: Organisation) {
    state.organisations.push(org);
  },
  updateOrg(
    state: { organisations: Organisation[] },
    updatedOrg: Organisation,
  ): void {
    state.organisations = state.organisations.map((org) =>
      org.uuid === updatedOrg.uuid ? updatedOrg : org,
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
